import type { ATSScore, ResumeData } from "@/types/resume";

type NormalizeOptions = {
  hasTargetJob: boolean;
};

// AI returns scores with different field names - this maps them to our ATSScore type
interface RawAIScore {
  overall?: number;
  formatting?: number;
  content?: number;
  structure?: number;
  keywords?: number;
  keywordMatch?: number;
  suggestions?: string[];
}

const clamp = (v: number) => Math.max(0, Math.min(100, Math.round(v)));

const normalizeTitle = (title: string) => title.trim().toLowerCase();

const hasCustomSection = (data: ResumeData, titleIncludes: RegExp) => {
  return (data.customSections ?? []).some((s) => titleIncludes.test(normalizeTitle(s.title || "")));
};

const hasNonEmptySummary = (data: ResumeData) => (data.summary ?? "").trim().length >= 50;

const hasCoreStrengths = (data: ResumeData) => (data.coreStrengths ?? []).some((c) => (c.skills ?? "").trim().length > 0);

const dedupe = (items: string[]) => {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of items) {
    const s = (raw ?? "").trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
};

export const normalizeATSScore = (score: ATSScore | RawAIScore, resumeData: ResumeData, opts: NormalizeOptions): ATSScore => {
  const hasProjects = hasCustomSection(resumeData, /project/);
  const hasCerts = hasCustomSection(resumeData, /(certificat|license)/);

  // Map AI/edge-function field names to our ATSScore type
  const rawKeywordMatch = clamp((score as RawAIScore).keywords ?? (score as ATSScore).keywordMatch ?? 0);
  const rawStructure = clamp((score as ATSScore).structure ?? (score as RawAIScore).content ?? 0);
  const formatting = clamp((score as ATSScore).formatting ?? (score as RawAIScore).formatting ?? 0);
  const rawOverall = clamp((score as ATSScore).overall ?? (score as RawAIScore).overall ?? 0);

  const suggestions = dedupe(score.suggestions ?? []).filter((s) => {
    const lc = s.toLowerCase();
    const isAddSuggestion = lc.includes(" add ") || lc.startsWith("add ") || lc.includes("consider adding") || lc.includes("include a");

    // Only suppress section-add suggestions when that section already exists
    if (hasProjects && /\badd\b.{0,20}\bproject/i.test(s)) return false;
    if (hasCerts && /\badd\b.{0,20}\bcertif/i.test(s)) return false;
    if (hasNonEmptySummary(resumeData) && isAddSuggestion && /\bsummary\b/.test(lc)) return false;
    if (hasCoreStrengths(resumeData) && isAddSuggestion && /\bskills? section\b/.test(lc)) return false;

    return true;
  });

  // Keyword match is only meaningful when a target job description is provided.
  const keywordMatch = opts.hasTargetJob ? rawKeywordMatch : 0;

  // Overall score:
  // - With target job: use the edge function's calibrated score (includes keyword alignment)
  // - Without target job: recompute from formatting + structure only (exclude any keyword contribution)
  //   If edge function returned a valid overall, we still recompute to guarantee no keyword inflation.
  const overall = opts.hasTargetJob
    ? (rawOverall > 0 ? rawOverall : clamp(Math.round((formatting + rawStructure) / 2)))
    : clamp(Math.round((formatting + rawStructure) / 2));

  return {
    overall,
    formatting,
    structure: rawStructure,
    keywordMatch,
    suggestions,
  };
};

